import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Package, User, ArrowLeft, Loader2, Search } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    fetchOrder();
  }, [orderId, navigate]);

  const fetchOrder = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      console.error("Order fetch error:", err);
      if (err.response?.status === 404) {
        setError("Commande non trouvée");
        toast.error("Commande non trouvée");
      } else {
        setError(err.response?.data?.detail || "Erreur lors de la récupération");
        toast.error("Erreur lors de la récupération de la commande");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/search");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-16 h-16 text-[#e5007c] animate-spin" />
        <p className="mt-4 text-xl font-bold uppercase tracking-wider">
          Chargement...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <header className="p-4 border-b-2 border-black">
          <Button
            onClick={handleBack}
            variant="ghost"
            className="h-12 px-4 font-bold uppercase tracking-wide border-2 border-black hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            RETOUR
          </Button>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500 border-2 border-black flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl text-white">!</span>
            </div>
            <h2 className="text-2xl font-black uppercase mb-2">ERREUR</h2>
            <p className="text-lg text-gray-600">{error}</p>
            <Button
              onClick={handleBack}
              className="btn-primary mt-6"
              data-testid="retry-btn"
            >
              NOUVELLE RECHERCHE
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="p-4 border-b-2 border-black flex items-center justify-between">
        <Button
          onClick={handleBack}
          variant="ghost"
          className="h-12 px-4 font-bold uppercase tracking-wide border-2 border-black hover:bg-gray-100"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          RETOUR
        </Button>
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
            Commande
          </p>
          <p className="text-lg font-black">
            {order?.reference || `#${orderId}`}
          </p>
        </div>
        <Button
          onClick={() => navigate("/search")}
          className="h-12 px-4 font-bold uppercase tracking-wide bg-[#e5007c] text-white border-2 border-black hover:bg-[#c4006a]"
          data-testid="other-order-btn"
        >
          <Search className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">AUTRE</span>
        </Button>
      </header>

      {/* Split Screen Buttons */}
      <main className="flex-1 flex flex-col">
        {/* DESTINATAIRE - Pink Section */}
        <button
          onClick={() => navigate(`/details/destinataire/${orderId}`)}
          className="flex-1 split-top flex flex-col items-center justify-center p-8 active:opacity-90 transition-opacity touch-target"
          data-testid="btn-destinataire"
        >
          <div className="w-24 h-24 bg-white border-4 border-black flex items-center justify-center mb-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            <Package className="w-12 h-12 text-[#e5007c]" />
          </div>
          <h2 
            className="text-4xl sm:text-5xl font-black uppercase tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            DESTINATAIRE
          </h2>
          <p className="text-lg mt-2 uppercase tracking-wider opacity-90">
            Adresse de livraison
          </p>
        </button>

        {/* EXPEDITEUR - Black Section */}
        <button
          onClick={() => navigate(`/details/expediteur/${orderId}`)}
          className="flex-1 split-bottom flex flex-col items-center justify-center p-8 active:opacity-90 transition-opacity touch-target"
          data-testid="btn-expediteur"
        >
          <div className="w-24 h-24 bg-white border-4 border-[#e5007c] flex items-center justify-center mb-6 shadow-[6px_6px_0px_0px_rgba(229,0,124,1)]">
            <User className="w-12 h-12 text-black" />
          </div>
          <h2 
            className="text-4xl sm:text-5xl font-black uppercase tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            EXPEDITEUR
          </h2>
          <p className="text-lg mt-2 uppercase tracking-wider opacity-90">
            Adresse de facturation
          </p>
        </button>
      </main>
    </div>
  );
}
